import {useEffect} from 'react';

import {useTranslation} from 'react-i18next';

import {Grid, Typography} from '@mui/material';

import {SetOfQuestions} from '../cultural-categories/SetOfQuestions';
import {
  surveySectionDescriptionStyles,
  surveySectionTitleStyles,
} from '../surveyQuestionnaireTypography';

import {EmpionButton} from '../../../../../../../components/EmpionButton/EmpionButton';
import QuestionNavigator from '../../../../../../../components/Navigator/QuestionNavigator';
import {useQuestionnaireNavigation} from '../../../../../../../hooks/useQuestionnaireNavigation';
import {Answer} from '../../../../../../../lib/api/RequestBodies/features/company/Questionnaires/QuestionnaireRequestBodies';
import {HappinessItem, Item} from '../../../types/SurveyQuestionnaireTypes';

interface Props<T extends HappinessItem | Item> {
  items: T[][];
  title: string;
  subtitle?: {
    top: string;
    bottom: string;
  };
  onSubmit: (answers: Answer[]) => void;
}

export const QuestionnaireSlides = <T extends HappinessItem | Item>({items, onSubmit, title, subtitle}: Props<T>) => {
  const {t} = useTranslation();
  const {onSectionIndexChange, questionSections, answerSections, onAnswer, sections, sectionIndex} =
    useQuestionnaireNavigation(items);

  const onComplete = () => {
    const answers: Answer[] = answerSections.flat();

    onSubmit(answers);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    scrollToTop();
  }, [sectionIndex]);

  const isSingleSectionForm = questionSections.length === 1;

  return (
    <Grid container justifyContent='center'>
      {title && (
        <Grid item xs={12}>
          <Typography variant='h2' gutterBottom align='center' sx={surveySectionTitleStyles}>
            {title}
          </Typography>
        </Grid>
      )}

      {subtitle && (
        <Grid item container justifyContent={'center'} mt={2}>
          <Typography variant='body1' mb={2} align='center' sx={surveySectionDescriptionStyles}>
            {subtitle.top}
          </Typography>
          <Typography variant='body1' align='center' sx={surveySectionDescriptionStyles}>
            {subtitle.bottom}
          </Typography>
        </Grid>
      )}

      <SetOfQuestions
        questions={questionSections[sectionIndex] || []}
        answers={answerSections[sectionIndex]}
        onAnswer={onAnswer}
      />
      {isSingleSectionForm ? (
        <Grid sx={{textAlign: 'center'}}>
          <EmpionButton onClick={onComplete} disabled={!sections[0].isFinished}>
            {t('shared.formSubmit.submit')}
          </EmpionButton>
        </Grid>
      ) : (
        <QuestionNavigator
          sections={sections}
          currentPosition={sectionIndex}
          onIndexChange={onSectionIndexChange}
          onComplete={onComplete}
        />
      )}
    </Grid>
  );
};
